<section class="min-h-screen">
  <!-- Page Header -->
  <div class="mb-6 flex items-start justify-between">
    <div>
      <h1 class="text-2xl font-bold tracking-tight text-primary-800 dark:text-primary-400">Loan Applications</h1>
      <p class="text-xs text-slate-500 dark:text-slate-400 mt-1">Registry for all incoming loan portfolio requests</p>
    </div>
    <div class="flex items-center gap-2 px-3 py-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg shadow-sm">
      <span class="text-[11px] font-bold text-slate-700 dark:text-slate-300">{{ loans.meta.total || 0 }}</span>
      <span class="text-[10px] text-slate-400 uppercase tracking-widest font-semibold">Total</span>
    </div>
  </div>

  <!-- Operational Registry Tools -->
  <div class="flex flex-wrap items-center justify-between gap-4 mb-5">
    <div class="flex-1 min-w-[300px] max-w-md">
      <form [formGroup]="searchQuery">
        <app-search [placeholder]="'Search by ID, name, or institution reference...'"
          formControlName="search"></app-search>
      </form>
    </div>
    <div class="flex items-center gap-2">
      <app-filters (onSaveEvent)="filterApplications($event)" (resetClickEvent)="resetFilters(); loadLoanApplications()"></app-filters>
      <div class="h-6 w-[1px] bg-slate-200 dark:bg-slate-700 mx-1"></div>
      <button (click)="loadLoanApplications()"
        class="px-3 py-1.5 rounded-lg text-slate-600 dark:text-slate-400 hover:text-slate-800 dark:text-slate-100 bg-white dark:bg-slate-800 hover:bg-slate-50 dark:hover:bg-slate-700 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700 transition-colors flex items-center gap-1.5 text-xs font-semibold"
        title="Refresh">
        <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
            d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
        </svg>
        <span>Refresh</span>
      </button>
      <button type="button" (click)="toggleAutoRefresh()"
        [class]="autoRefreshEnabled
          ? 'px-3 py-1.5 rounded-lg text-white bg-primary-800 hover:bg-primary-900 border border-primary-800 transition-colors flex items-center gap-1.5 text-xs font-semibold'
          : 'px-3 py-1.5 rounded-lg text-slate-600 dark:text-slate-400 hover:text-slate-800 dark:text-slate-100 bg-white dark:bg-slate-800 hover:bg-slate-50 dark:hover:bg-slate-700 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700 transition-colors flex items-center gap-1.5 text-xs font-semibold'"
        [title]="autoRefreshEnabled ? 'Auto-refresh on (every 60s)' : 'Auto-refresh off'">
        @if (autoRefreshEnabled) {
          <span class="relative flex h-2 w-2">
            <span class="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75"></span>
            <span class="relative inline-flex rounded-full h-2 w-2 bg-white"></span>
          </span>
        } @else {
          <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        }
        <span>Auto-refresh</span>
      </button>
      <div class="flex items-center border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden">
        <button type="button" (click)="viewMode = 'table'" [class]="viewMode === 'table' ? 'p-1.5 bg-primary-800 text-white' : 'p-1.5 bg-white dark:bg-slate-800 text-slate-400 hover:text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-700 dark:bg-slate-800/60'" title="Table view">
          <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 10h16M4 14h16M4 18h16"/></svg>
        </button>
        <button type="button" (click)="viewMode = 'grid'" [class]="viewMode === 'grid' ? 'p-1.5 bg-primary-800 text-white' : 'p-1.5 bg-white dark:bg-slate-800 text-slate-400 hover:text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-700 dark:bg-slate-800/60'" title="Grid view">
          <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 5a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1H5a1 1 0 01-1-1V5zm10 0a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1V5zM4 15a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1H5a1 1 0 01-1-1v-4zm10 0a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1v-4z"/></svg>
        </button>
      </div>
      <div class="h-6 w-[1px] bg-slate-200 dark:bg-slate-700 mx-1"></div>
      <app-export-button (onExportEvent)="handleExport($event)"></app-export-button>
    </div>
  </div>

  <!-- Main Registry Table -->
  @if (viewMode === 'table') {
  <div class="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl overflow-x-auto">
    <div class="w-full min-w-[1100px]">

      <!-- Table Header -->
      <div class="grid grid-cols-12 gap-x-3 bg-slate-50 dark:bg-slate-800/60 border-b border-slate-200 dark:border-slate-700 px-6 py-3">
        <div class="col-span-1 text-left"><p class="text-xs font-semibold text-slate-400">#</p></div>
        <div class="col-span-1 text-left"><p class="text-xs font-semibold text-slate-400">Date</p></div>
        <div class="col-span-2 text-left"><p class="text-xs font-semibold text-slate-400">Applicant</p></div>
        <div class="col-span-1 text-left"><p class="text-xs font-semibold text-slate-400">Ref / ID</p></div>
        <div class="col-span-1 text-left"><p class="text-xs font-semibold text-slate-400">Product</p></div>
        <div class="col-span-1 text-left"><p class="text-xs font-semibold text-slate-400">Principal</p></div>
        <div class="col-span-1 text-left"><p class="text-xs font-semibold text-slate-400">Interest</p></div>
        <div class="col-span-1 text-left"><p class="text-xs font-semibold text-slate-400">Tenure</p></div>
        <div class="col-span-2 text-left"><p class="text-xs font-semibold text-slate-400">Status</p></div>
        <div class="col-span-1 text-center"><p class="text-xs font-semibold text-slate-400">Actions</p></div>
      </div>

      <!-- Table Rows -->
      <div class="min-h-[400px]">
        @if (isLoading) {
        <div class="flex items-center justify-center py-20">
          <app-progress-loader></app-progress-loader>
        </div>
        } @else if (loans.data && loans.data.length > 0) {
        @for (loan of loans.data; track loan.id; let i = $index) {
        <div class="grid grid-cols-12 gap-x-3 px-6 py-3.5 border-b border-slate-100 dark:border-slate-700/50 last:border-0 hover:bg-slate-50 dark:hover:bg-slate-700 items-center transition-colors cursor-pointer"
          (click)="viewApplication(loan)">

          <!-- # -->
          <div class="col-span-1 text-left">
            <span class="text-sm text-slate-400">{{ ((loans.meta.page || 1) - 1) * (loans.meta.perPage || 10) + i + 1 }}</span>
          </div>

          <!-- Date -->
          <div class="col-span-1 text-left">
            <p class="text-sm text-slate-700 dark:text-slate-300">{{ (loan.created_at || loan.createdAt) | date:'dd MMM, yyyy' }}</p>
          </div>

          <!-- Applicant -->
          <div class="col-span-2 min-w-0">
            <div class="flex items-center justify-start gap-1.5 min-w-0">
              <p class="text-sm font-medium text-slate-900 dark:text-white truncate">
                {{ loan.user?.firstName || loan.member?.first_name || '—' }}
                {{ loan.user?.otherName ? loan.user?.otherName + ' ' : '' }}{{ loan.user?.lastName || loan.member?.last_name || '' }}
              </p>
              @if (loan.counterOffer) {
              <span class="inline-flex items-center text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-600 border border-amber-300 flex-shrink-0">Counter</span>
              }
            </div>
            <p class="text-xs text-slate-400 truncate text-left">{{ loan.user?.mobileNumber || '—' }}</p>
          </div>

          <!-- Ref / ID -->
          <div class="col-span-1 min-w-0 flex items-center justify-start gap-1 group/ref">
            <p class="text-xs font-mono text-slate-600 dark:text-slate-400 truncate">{{ loan.reference?.substring(0, 14) || loan.id?.substring(0, 8) || '—' }}</p>
            @if (loan.reference) {
            <button (click)="copyReference(loan.reference, $event)"
              class="flex-shrink-0 p-0.5 rounded opacity-0 group-hover/ref:opacity-100 transition-opacity"
              [class]="copiedRef === loan.reference ? 'text-emerald-500' : 'text-slate-400 hover:text-slate-700 dark:text-slate-300'"
              [title]="copiedRef === loan.reference ? 'Copied!' : 'Copy reference'">
              @if (copiedRef === loan.reference) {
              <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M5 13l4 4L19 7"/></svg>
              } @else {
              <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><rect x="9" y="9" width="13" height="13" rx="2" ry="2" stroke-width="2"/><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"/></svg>
              }
            </button>
            }
          </div>

          <!-- Product / Institution -->
          <div class="col-span-1 min-w-0 text-left">
            <p class="text-sm text-slate-700 dark:text-slate-300 truncate">{{ loan.product?.name || '—' }}</p>
            <p class="text-xs text-slate-400 truncate">{{ loan.institution?.name || loan.institution || '—' }}</p>
          </div>

          <!-- Principal -->
          <div class="col-span-1 text-left">
            <p class="text-sm font-semibold text-slate-900 dark:text-white">₵{{ (loan.amount || loan.principal_amount || 0) | number:'1.0-0' }}</p>
          </div>

          <!-- Interest -->
          <div class="col-span-1 text-left">
            <p class="text-sm text-slate-600 dark:text-slate-400">₵{{ (loan.interest || 0) | number:'1.2-2' }}</p>
          </div>

          <!-- Tenure -->
          <div class="col-span-1 text-left">
            <p class="text-sm text-slate-700 dark:text-slate-300">{{ loan.tenure }}<span class="text-xs text-slate-400 ml-0.5">{{ loan.product?.tenureType === 'DAILY' ? 'D' : 'M' }}</span></p>
          </div>

          <!-- Status -->
          <div class="col-span-2 text-left">
            <span class="text-xs font-semibold px-2.5 py-1 rounded-md border whitespace-nowrap" [class]="loan.status | loanStatusStyle">
              {{ loan.status | loanStatusLabel }}
            </span>
          </div>

          <!-- Actions -->
          <div class="col-span-1 flex justify-center" (click)="$event.stopPropagation()">
            <button (click)="viewApplication(loan)" class="px-2 py-1 text-xs font-semibold text-slate-600 dark:text-slate-400 border border-slate-200 dark:border-slate-700 rounded hover:bg-slate-50 dark:hover:bg-slate-700 dark:bg-slate-800/60 hover:border-slate-300 dark:border-slate-600 transition-colors">
              View
            </button>
          </div>

        </div>
        }
        } @else {
        <div class="flex flex-col items-center justify-center py-24 px-6 text-center">
          <div class="max-w-xs">
            <div class="w-12 h-12 bg-slate-50 dark:bg-slate-800/60 rounded-full flex items-center justify-center mx-auto mb-4 border border-slate-100 dark:border-slate-700/50">
              <span class="text-xl text-slate-300">!</span>
            </div>
            <h3 class="text-sm font-bold text-slate-900 dark:text-white">No Results Found</h3>
            <p class="text-xs text-slate-500 dark:text-slate-400 mt-2 leading-relaxed">No applications match your current filters.</p>
            <button (click)="loadLoanApplications()" class="mt-5 text-xs font-semibold text-primary-800 dark:text-primary-400 border border-primary-800/20 px-3 py-1.5 rounded-lg hover:bg-primary-800/5 transition-colors">
              Refresh
            </button>
          </div>
        </div>
        }
      </div>
    </div>
  </div>
  } @else {
  <!-- Grid View -->
  <div class="min-h-[400px]">
    @if (isLoading) {
    <div class="flex items-center justify-center py-20"><app-progress-loader></app-progress-loader></div>
    } @else if (loans.data && loans.data.length > 0) {
    <div class="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
      @for (loan of loans.data; track loan.id) {
      <div (click)="viewApplication(loan)"
        class="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl p-5 hover:shadow-md transition-shadow cursor-pointer group">
        <div class="flex items-start justify-between mb-3">
          <div class="min-w-0">
            <p class="text-sm font-bold text-slate-900 dark:text-white truncate">
              {{ loan.user?.firstName || '—' }} {{ loan.user?.lastName || '' }}
            </p>
            <p class="text-xs text-slate-400 truncate mt-0.5">{{ loan.user?.email || '—' }}</p>
          </div>
          <span class="ml-2 shrink-0 text-xs font-bold px-2 py-1 rounded-md uppercase tracking-normal whitespace-nowrap border"
            [class]="loan.status | loanStatusStyle">{{ loan.status | loanStatusLabel }}</span>
        </div>
        <div class="space-y-1.5 mb-4">
          <div class="flex items-center justify-between">
            <span class="text-[11px] text-slate-400 uppercase tracking-widest font-semibold">Amount</span>
            <span class="text-sm font-bold text-slate-900 dark:text-white">₵{{ (loan.amount || 0) | number:'1.0-0' }}</span>
          </div>
          <div class="flex items-center justify-between">
            <span class="text-[11px] text-slate-400 uppercase tracking-widest font-semibold">Product</span>
            <span class="text-xs font-semibold text-slate-700 dark:text-slate-300 truncate max-w-[140px]">{{ loan.product?.name || '—' }}</span>
          </div>
          <div class="flex items-center justify-between">
            <span class="text-[11px] text-slate-400 uppercase tracking-widest font-semibold">Institution</span>
            <span class="text-xs text-slate-500 dark:text-slate-400 truncate max-w-[140px]">{{ loan.institution?.name || loan.institution || '—' }}</span>
          </div>
          <div class="flex items-center justify-between">
            <span class="text-[11px] text-slate-400 uppercase tracking-widest font-semibold">Tenure</span>
            <span class="text-xs font-semibold text-slate-700 dark:text-slate-300">{{ loan.tenure }} {{ loan.product?.tenureType === 'DAILY' ? 'day' + (loan.tenure !== 1 ? 's' : '') : 'month' + (loan.tenure !== 1 ? 's' : '') }}</span>
          </div>
        </div>
        <div class="flex items-center justify-between pt-3 border-t border-slate-100 dark:border-slate-700/50">
          <p class="text-[11px] text-slate-400">{{ (loan.created_at || loan.createdAt) | date:'dd MMM yyyy' }}</p>
          <div class="flex items-center gap-1" (click)="$event.stopPropagation()">
            @if (loan.status === 'PENDING_APPROVAL' && !loan.counterOffer) {
            @if (permissionService.canApprove()) {
            <button (click)="handleAction(loan, 'APPROVE')" class="p-1.5 rounded text-slate-400 hover:text-emerald-600 hover:bg-slate-100 dark:hover:bg-slate-700 dark:bg-slate-900 transition-colors" title="Approve">
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M5 13l4 4L19 7"/></svg>
            </button>
            <button (click)="handleAction(loan, 'REJECT')" class="p-1.5 rounded text-slate-400 hover:text-rose-600 hover:bg-slate-100 dark:hover:bg-slate-700 dark:bg-slate-900 transition-colors" title="Reject">
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M6 18L18 6M6 6l12 12"/></svg>
            </button>
            @if (loan.requiresAcceptance !== false) {
            <button (click)="handleAction(loan, 'COUNTER')" class="p-1.5 rounded text-slate-400 hover:text-amber-600 hover:bg-slate-100 dark:hover:bg-slate-700 dark:bg-slate-900 transition-colors" title="Counter">
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4"/></svg>
            </button>
            }
            }
            }
            @if ((loan.status === 'APPROVED' || loan.status === 'PENDING_DISBURSEMENT') && permissionService.canDisburse()) {
            <button (click)="handleAction(loan, 'DISBURSE')" class="p-1.5 rounded text-slate-400 hover:text-blue-600 hover:bg-slate-100 dark:hover:bg-slate-700 dark:bg-slate-900 transition-colors" title="Disburse">
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>
            </button>
            }
            <button (click)="viewApplication(loan)" class="px-2 py-1 text-xs font-semibold text-slate-600 dark:text-slate-400 border border-slate-200 dark:border-slate-700 rounded hover:bg-slate-50 dark:hover:bg-slate-700 dark:bg-slate-800/60 hover:border-slate-300 dark:border-slate-600 transition-colors">View</button>
          </div>
        </div>
      </div>
      }
    </div>
    } @else {
    <div class="flex flex-col items-center justify-center py-24 text-center">
      <p class="text-sm font-bold text-slate-800 dark:text-slate-100">No applications found</p>
    </div>
    }
  </div>
  }

  <!-- Pagination -->
  @if (!isLoading && loans.data && loans.data.length > 0) {
  <app-pagination [totalItems]="loans.meta.total || 0" [itemsPerPage]="loans.meta.perPage || 10"
    [currentPage]="loans.meta.page || 1" (pageChange)="onPageChange($event)"></app-pagination>
  }

  <!-- Approve Confirmation Dialog -->
  @if (showApproveDialog) {
  <app-confirmation-dialog
    [title]="'Approve Loan Application'"
    [message]="'Are you sure you want to approve this loan application? This action will proceed with disbursement.'"
    [confirmText]="'Approve'"
    [cancelText]="'Cancel'"
    [type]="'info'"
    (onConfirm)="confirmApprove()"
    (onCancel)="cancelApprove()">
  </app-confirmation-dialog>
  }

  <!-- Reject Dialog with Reason Input -->
  @if (showRejectDialog) {
  <div class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70] p-4 backdrop-blur-md animate-in fade-in duration-300">
    <div class="bg-white dark:bg-slate-800 rounded-lg shadow-2xl w-full max-w-md overflow-hidden animate-in zoom-in-95 duration-300" (click)="$event.stopPropagation()">
      <div class="p-5">
        <div class="flex items-start gap-3">
          <div class="flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center bg-rose-50 dark:bg-rose-950/40">
            <svg class="w-5 h-5 text-rose-600 dark:text-rose-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
          <div class="flex-1">
            <h3 class="text-sm font-bold text-slate-900 dark:text-white mb-1">Reject Loan Application</h3>
            <p class="text-xs text-slate-500 dark:text-slate-400 mb-4">Please provide a reason for rejecting this application.</p>
            <textarea
              [(ngModel)]="rejectionReason"
              rows="3"
              placeholder="Enter rejection reason..."
              class="w-full px-3 py-2 text-xs border border-slate-200 dark:border-slate-700 rounded focus:outline-none focus:ring-1 focus:ring-rose-400 resize-none">
            </textarea>
          </div>
        </div>
      </div>
      <div class="bg-slate-50 dark:bg-slate-800/60 px-5 py-3 flex items-center justify-end gap-2">
        <button (click)="cancelReject()"
          class="px-3 py-2 text-xs font-medium text-slate-700 dark:text-slate-300 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded hover:bg-slate-50 dark:hover:bg-slate-700 dark:bg-slate-800/60 transition-colors">
          Cancel
        </button>
        <button (click)="confirmReject()" [disabled]="!rejectionReason.trim()"
          class="px-3 py-2 text-xs font-medium text-white bg-rose-600 rounded hover:bg-rose-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
          Reject Application
        </button>
      </div>
    </div>
  </div>
  }

  <!-- Counter Offer Dialog -->
  @if (showCounterDialog) {
  <div class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70] p-4 backdrop-blur-md animate-in fade-in duration-300">
    <div class="bg-white dark:bg-slate-800 rounded-lg shadow-2xl w-full max-w-md overflow-hidden animate-in zoom-in-95 duration-300" (click)="$event.stopPropagation()">
      <div class="p-5">
        <div class="flex items-start gap-3">
          <div class="flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center bg-amber-50 dark:bg-amber-950/40">
            <svg class="w-5 h-5 text-amber-600 dark:text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
            </svg>
          </div>
          <div class="flex-1">
            <h3 class="text-sm font-bold text-slate-900 dark:text-white mb-1">Counter Offer</h3>
            <p class="text-xs text-slate-500 dark:text-slate-400 mb-4">Propose a revised amount and tenure for this loan application.</p>
            <div class="space-y-3">
              <div>
                <label class="text-xs font-medium text-slate-700 dark:text-slate-300 block mb-1">Amount <span class="text-error">*</span></label>
                <input type="number" [(ngModel)]="counterForm.amount" min="0" placeholder="Enter counter amount"
                  class="w-full px-3 py-2 text-xs border border-slate-200 dark:border-slate-700 rounded focus:outline-none focus:ring-1 focus:ring-amber-400" />
              </div>
              <div>
                <label class="text-xs font-medium text-slate-700 dark:text-slate-300 block mb-1">Tenure (months) <span class="text-error">*</span></label>
                <input type="number" [(ngModel)]="counterForm.tenure" min="1" placeholder="Enter tenure in months"
                  class="w-full px-3 py-2 text-xs border border-slate-200 dark:border-slate-700 rounded focus:outline-none focus:ring-1 focus:ring-amber-400" />
              </div>
              <div>
                <label class="text-xs font-medium text-slate-700 dark:text-slate-300 block mb-1">Comment</label>
                <textarea [(ngModel)]="counterForm.comment" rows="3" placeholder="Optional comment..."
                  class="w-full px-3 py-2 text-xs border border-slate-200 dark:border-slate-700 rounded focus:outline-none focus:ring-1 focus:ring-amber-400 resize-none">
                </textarea>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="bg-slate-50 dark:bg-slate-800/60 px-5 py-3 flex items-center justify-end gap-2">
        <button (click)="cancelCounter()"
          class="px-3 py-2 text-xs font-medium text-slate-700 dark:text-slate-300 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded hover:bg-slate-50 dark:hover:bg-slate-700 dark:bg-slate-800/60 transition-colors">
          Cancel
        </button>
        <button (click)="confirmCounter()" [disabled]="!counterForm.amount || !counterForm.tenure"
          class="px-3 py-2 text-xs font-medium text-white bg-amber-600 rounded hover:bg-amber-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
          Submit Counter
        </button>
      </div>
    </div>
  </div>
  }

  <!-- Disburse Confirmation Dialog -->
  @if (showDisburseDialog) {
  <app-confirmation-dialog
    [title]="'Disburse Loan'"
    [message]="'Are you sure you want to disburse this loan? Funds will be released to the applicant.'"
    [confirmText]="'Disburse'"
    [cancelText]="'Cancel'"
    [type]="'info'"
    (onConfirm)="confirmDisburse()"
    (onCancel)="cancelDisburse()">
  </app-confirmation-dialog>
  }

  <!-- Modal Overlay -->
  @if (showModal && currentLoan) {
  <div class="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/40 backdrop-blur-sm p-4">
    <div class="w-full max-w-4xl bg-white dark:bg-slate-800 rounded-2xl shadow-2xl flex flex-col overflow-hidden" style="max-height: 90vh;" (click)="$event.stopPropagation()">

      <!-- Modal Header -->
      <div class="flex items-center justify-between px-6 py-4 border-b border-slate-200 dark:border-slate-700 flex-shrink-0">
        <div>
          <p class="text-xs font-semibold text-slate-400">Loan Application</p>
          <h2 class="text-lg font-bold text-slate-900 dark:text-white mt-0.5">
            #{{ currentLoan.reference ?? currentLoan.id?.substring(0, 8)?.toUpperCase() }}
          </h2>
        </div>
        <div class="flex items-center gap-2">
          <span class="text-[10px] font-bold px-2.5 py-1 rounded border uppercase tracking-wide"
            [class]="currentLoan.status | loanStatusStyle">
            {{ currentLoan.status | loanStatusLabel }}
          </span>
          @if (currentLoan.status === 'PENDING_APPROVAL' && !currentLoan.counterOffer) {
          @if (permissionService.canApprove()) {
          <button (click)="handleAction(currentLoan, 'APPROVE')"
            class="px-3 py-1.5 text-xs font-semibold text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-200 dark:border-emerald-900 rounded-lg hover:bg-emerald-100 dark:hover:bg-emerald-950/60 transition-colors">
            Approve
          </button>
          <button (click)="handleAction(currentLoan, 'REJECT')"
            class="px-3 py-1.5 text-xs font-semibold text-rose-700 dark:text-rose-400 bg-rose-50 dark:bg-rose-950/40 border border-rose-200 dark:border-rose-900 rounded-lg hover:bg-rose-100 dark:hover:bg-rose-950/60 transition-colors">
            Reject
          </button>
          @if (currentLoan.requiresAcceptance !== false) {
          <button (click)="handleAction(currentLoan, 'COUNTER')"
            class="px-3 py-1.5 text-xs font-semibold text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-900 rounded-lg hover:bg-amber-100 dark:hover:bg-amber-950/60 transition-colors">
            Counter
          </button>
          }
          }
          }
          @if (currentLoan.status === 'ACCEPTED' && permissionService.canApprove()) {
          <button (click)="handleAction(currentLoan, 'APPROVE')"
            class="px-3 py-1.5 text-xs font-semibold text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-200 dark:border-emerald-900 rounded-lg hover:bg-emerald-100 dark:hover:bg-emerald-950/60 transition-colors">
            Approve
          </button>
          }
          @if ((currentLoan.status === 'APPROVED' || currentLoan.status === 'PENDING_DISBURSEMENT') && permissionService.canDisburse()) {
          <button (click)="handleAction(currentLoan, 'DISBURSE')"
            class="px-3 py-1.5 text-xs font-semibold text-blue-700 dark:text-blue-400 bg-blue-50 dark:bg-blue-950/40 border border-blue-200 dark:border-blue-900 rounded-lg hover:bg-blue-100 dark:hover:bg-blue-950/60 transition-colors">
            Disburse
          </button>
          }
          @if (currentLoan.status === 'DISBURSED') {
          <button (click)="openRepayModal()"
            class="px-3 py-1.5 rounded-lg text-white text-xs font-semibold flex items-center gap-1.5 transition-colors bg-primary-800 hover:bg-primary-900">
            <svg xmlns="http://www.w3.org/2000/svg" class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
            </svg>
            Record Repayment
          </button>
          }
          <button (click)="closeModal()" class="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 dark:bg-slate-900 transition-colors">
            <svg class="w-5 h-5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
            </svg>
          </button>
        </div>
      </div>

      <!-- Tab Bar -->
      <div class="flex border-b border-slate-200 dark:border-slate-700 flex-shrink-0 bg-white dark:bg-slate-800">
        <button (click)="activeModalTab = 'details'"
          class="px-6 py-3 text-xs font-bold uppercase tracking-wide border-b-2 transition-all"
          [class]="activeModalTab === 'details' ? 'border-[#0D76BA] text-[#0D76BA]' : 'border-transparent text-slate-400 hover:text-slate-600 dark:text-slate-400'">
          Loan Details
        </button>
        <button (click)="activeModalTab = 'repayments'"
          class="px-6 py-3 text-xs font-bold uppercase tracking-wide border-b-2 transition-all flex items-center gap-2"
          [class]="activeModalTab === 'repayments' ? 'border-[#0D76BA] text-[#0D76BA]' : 'border-transparent text-slate-400 hover:text-slate-600 dark:text-slate-400'">
          Repayments
          @if (!isLoadingRepayments) {
          <span class="text-[10px] font-bold px-1.5 py-0.5 rounded-full"
            [class]="activeModalTab === 'repayments' ? 'bg-blue-100 text-blue-600' : 'bg-slate-100 dark:bg-slate-900 text-slate-500 dark:text-slate-400'">
            {{ loanRepayments.length }}
          </span>
          }
        </button>
      </div>

      <!-- Tab Content -->
      <div class="overflow-y-auto flex-1">

        <!-- Details Tab -->
        @if (activeModalTab === 'details') {
        <app-view-new-application [loanInfo]="currentLoan" [showHeader]="false" (onClose)="closeModal()"></app-view-new-application>
        }

        <!-- Repayments Tab -->
        @if (activeModalTab === 'repayments') {
        <div>
          <!-- Summary strip -->
          <div class="grid grid-cols-3 divide-x divide-slate-100 dark:divide-slate-700 border-b border-slate-200 dark:border-slate-700">
            <div class="px-6 py-4">
              <p class="text-xs font-semibold text-slate-400 mb-1">Total Repaid</p>
              <p class="text-lg font-bold text-emerald-600">₵{{ totalRepaid | number:'1.2-2' }}</p>
            </div>
            <div class="px-6 py-4">
              <p class="text-xs font-semibold text-slate-400 mb-1">Total Repayment</p>
              <p class="text-lg font-bold text-slate-800 dark:text-slate-100">₵{{ (currentLoan.totalRepayment ?? 0) | number:'1.2-2' }}</p>
            </div>
            <div class="px-6 py-4">
              <p class="text-xs font-semibold text-slate-400 mb-1">Balance</p>
              <p class="text-lg font-bold" [class]="repaymentBalance <= 0 ? 'text-emerald-600' : 'text-rose-600'">
                ₵{{ repaymentBalance | number:'1.2-2' }}
              </p>
            </div>
          </div>

          @if (isLoadingRepayments) {
          <div class="flex items-center justify-center py-16">
            <div class="w-6 h-6 border-2 border-slate-200 dark:border-slate-700 border-t-blue-500 rounded-full animate-spin"></div>
          </div>
          } @else if (loanRepayments.length === 0) {
          <div class="flex flex-col items-center justify-center py-20 text-center">
            <div class="w-12 h-12 bg-slate-50 dark:bg-slate-800/60 rounded-full flex items-center justify-center mb-3 border border-slate-100 dark:border-slate-700/50">
              <svg xmlns="http://www.w3.org/2000/svg" class="w-5 h-5 text-slate-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"/>
              </svg>
            </div>
            <p class="text-sm font-semibold text-slate-600 dark:text-slate-400">No repayments recorded yet</p>
            <p class="text-xs text-slate-400 mt-1">Repayments will appear here once recorded.</p>
          </div>
          } @else {
          <!-- Table header -->
          <div class="grid grid-cols-6 gap-4 px-6 py-3 bg-slate-50 dark:bg-slate-800/60 border-b border-slate-200 dark:border-slate-700">
            <p class="text-xs font-semibold text-slate-400">Date</p>
            <p class="text-xs font-semibold text-slate-400">Transaction ID</p>
            <p class="text-xs font-semibold text-slate-400">Type</p>
            <p class="text-xs font-semibold text-slate-400">Amount Paid</p>
            <p class="text-xs font-semibold text-slate-400">Comment</p>
            <p class="text-xs font-semibold text-slate-400">Recorded At</p>
          </div>
          @for (r of loanRepayments; track r.id) {
          <div class="grid grid-cols-6 gap-4 px-6 py-4 border-b border-slate-100 dark:border-slate-700/50 last:border-0 hover:bg-slate-50 dark:hover:bg-slate-700 dark:bg-slate-800/60 transition-colors items-center">
            <div>
              <p class="text-[12px] font-bold text-slate-700 dark:text-slate-300">{{ r.date | date:'d MMM yyyy' }}</p>
            </div>
            <div>
              <p class="text-[11px] font-mono text-slate-500 dark:text-slate-400 truncate" [title]="r.transactionId">{{ r.transactionId }}</p>
            </div>
            <div>
              @if (r.type === 'BANK') {
              <span class="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold bg-blue-50 dark:bg-blue-950/40 text-blue-600 dark:text-blue-400 border border-blue-100 dark:border-blue-900 uppercase tracking-wider">Bank</span>
              } @else if (r.type === 'MOMO') {
              <span class="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold bg-amber-50 dark:bg-amber-950/40 text-amber-600 dark:text-amber-400 border border-amber-100 dark:border-amber-900 uppercase tracking-wider">MoMo</span>
              } @else if (r.type === 'CASH') {
              <span class="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold bg-emerald-50 dark:bg-emerald-950/40 text-emerald-600 dark:text-emerald-400 border border-emerald-100 dark:border-emerald-900 uppercase tracking-wider">Cash</span>
              } @else {
              <span class="text-[10px] text-slate-400">—</span>
              }
            </div>
            <div>
              <p class="text-[13px] font-bold text-emerald-600">₵{{ +r.amount | number:'1.2-2' }}</p>
            </div>
            <div>
              <p class="text-[11px] text-slate-500 dark:text-slate-400 truncate">{{ r.comment || '—' }}</p>
            </div>
            <div>
              <p class="text-[11px] text-slate-500 dark:text-slate-400">{{ r.createdAt | date:'d MMM yyyy' }}</p>
              <p class="text-[10px] text-slate-400">{{ r.createdAt | date:'h:mm a' }}</p>
            </div>
          </div>
          }
          }
        </div>
        }

      </div>
    </div>
    <div class="absolute inset-0 -z-10" (click)="closeModal()"></div>
  </div>
  }

  <!-- Record Repayment Modal -->
  @if (showRepayModal && currentLoan) {
  <div class="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 backdrop-blur-sm px-4">
    <div class="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl w-full max-w-md p-6 space-y-5">
      <div class="flex items-center justify-between">
        <div>
          <h3 class="text-lg font-bold text-slate-900 dark:text-white">Record Repayment</h3>
          <p class="text-xs text-slate-500 dark:text-slate-400 mt-0.5">Loan: #{{ currentLoan.reference ?? currentLoan.id?.substring(0, 8)?.toUpperCase() }}</p>
        </div>
        <button (click)="closeRepayModal()"
          class="w-8 h-8 rounded-full bg-slate-100 dark:bg-slate-900 hover:bg-slate-200 dark:bg-slate-700 flex items-center justify-center transition-colors">
          <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4 text-slate-600 dark:text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <form [formGroup]="repayForm" (ngSubmit)="submitRepayment()" class="space-y-4">
        <div>
          <label class="block text-xs font-semibold text-slate-600 dark:text-slate-400 mb-1">Repayment Type <span class="text-error">*</span></label>
          <app-custom-select-input
            formControlName="type"
            [options]="repaymentTypes"
            [placeholder]="'Select repayment type'">
          </app-custom-select-input>
          @if (repayForm.get('type')?.invalid && repayForm.get('type')?.touched) {
          <p class="text-xs text-error mt-1">Repayment type is required</p>
          }
        </div>
        <div>
          <label class="block text-xs font-semibold text-slate-600 dark:text-slate-400 mb-1">Transaction ID <span class="text-error">*</span></label>
          <app-custom-input formControlName="transactionId" [placeholder]="'Enter transaction ID'" [isRequired]="true"></app-custom-input>
          @if (repayForm.get('transactionId')?.invalid && repayForm.get('transactionId')?.touched) {
          <p class="text-xs text-error mt-1">Transaction ID is required</p>
          }
        </div>
        <div>
          <label class="block text-xs font-semibold text-slate-600 dark:text-slate-400 mb-1">Amount (₵) <span class="text-error">*</span></label>
          <app-custom-input formControlName="amount" [type]="'number'" [placeholder]="'0.00'" [isRequired]="true"></app-custom-input>
          @if (repayForm.get('amount')?.invalid && repayForm.get('amount')?.touched) {
          <p class="text-xs text-error mt-1">A valid amount is required</p>
          }
        </div>
        <div>
          <label class="block text-xs font-semibold text-slate-600 dark:text-slate-400 mb-1">Date <span class="text-error">*</span></label>
          <app-custom-input formControlName="date" [type]="'date'" [isRequired]="true"></app-custom-input>
          @if (repayForm.get('date')?.invalid && repayForm.get('date')?.touched) {
          <p class="text-xs text-error mt-1">Date is required</p>
          }
        </div>
        <div>
          <label class="block text-xs font-semibold text-slate-600 dark:text-slate-400 mb-1">Comment</label>
          <app-custom-input formControlName="comment" [placeholder]="'Optional note'" [isTextarea]="true" [rows]="3"></app-custom-input>
        </div>
        <div class="flex gap-3 pt-2">
          <button type="button" (click)="closeRepayModal()"
            class="flex-1 h-11 rounded-xl border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-400 text-sm font-semibold hover:bg-slate-50 dark:hover:bg-slate-700 dark:bg-slate-800/60 transition-colors">
            Cancel
          </button>
          <button type="submit" [disabled]="isSubmittingRepay"
            class="flex-1 h-11 rounded-xl bg-primary-800 hover:bg-primary-900 text-white text-sm font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
            {{ isSubmittingRepay ? 'Saving...' : 'Record Repayment' }}
          </button>
        </div>
      </form>
    </div>
  </div>
  }
</section>